import React, { useMemo, useState } from 'react'
import {
  LinkButton,
  LINK_BUTTON_PREVIEW_STATE_LABELS,
  LINK_BUTTON_PREVIEW_STATES,
  LINK_BUTTON_SIZE_LABELS,
  LINK_BUTTON_SIZES,
  type LinkButtonPreviewState,
  type LinkButtonSize,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './LinkButtonShowcase.css'

const LinkButtonShowcase: React.FC = () => {
  const [label, setLabel] = useState('View details')
  const [size, setSize] = useState<LinkButtonSize>('md')
  const [underline, setUnderline] = useState(true)
  const [disabled, setDisabled] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<LinkButtonPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'link-button-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="link-button-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as LinkButtonPreviewState,
                  )
                }
              >
                {LINK_BUTTON_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {LINK_BUTTON_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'link-button-label',
            label: 'Label',
            control: (
              <input
                id="link-button-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'link-button-size',
            label: 'Size',
            control: (
              <select
                id="link-button-size"
                className="ui-field-control"
                value={size}
                onChange={(event) =>
                  setSize(event.target.value as LinkButtonSize)
                }
              >
                {LINK_BUTTON_SIZES.map((option) => (
                  <option key={option} value={option}>
                    {LINK_BUTTON_SIZE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'link-button-underline',
            label: 'Underline',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="link-button-underline"
                  type="checkbox"
                  checked={underline}
                  onChange={(event) => setUnderline(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'link-button-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="link-button-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'link-button-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="link-button-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, disabled, highlightedState, label, size, underline],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Link button"
        description="Plain-text action styled like a hyperlink for low-emphasis inline actions."
        stageClassName="ui-preview__stage--wide link-button-showcase__stage-wrap"
      >
        <div
          className={`link-button-showcase__sheet${
            darkMode ? ' link-button-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Plain-text hyperlink actions for low-emphasis inline CTAs.
            </p>
            <div
              className={`link-button-showcase__live${
                darkMode ? ' link-button-showcase__live--dark' : ''
              }`}
            >
              <LinkButton
                label={label}
                size={size}
                underline={underline}
                disabled={disabled}
                darkMode={darkMode}
              />
            </div>
          </section>

          <section className="link-button-showcase__matrix">
            <h3 className="ui-showcase-live__title">Sizes</h3>
            <div
              className={`link-button-showcase__row${
                darkMode ? ' link-button-showcase__row--dark' : ''
              }`}
            >
              <div className="link-button-showcase__size-row">
                {LINK_BUTTON_SIZES.map((option) => (
                  <div key={option} className="link-button-showcase__size-item">
                    <span className="link-button-showcase__label">
                      {LINK_BUTTON_SIZE_LABELS[option]}
                    </span>
                    <LinkButton
                      label="Link button"
                      size={option}
                      darkMode={darkMode}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="link-button-showcase__matrix">
            <h3 className="ui-showcase-live__title">States</h3>
            {LINK_BUTTON_PREVIEW_STATES.map((state) => (
              <div
                key={state}
                className={`link-button-showcase__row${
                  highlightedState === state
                    ? ' link-button-showcase__row--selected'
                    : ''
                }${darkMode ? ' link-button-showcase__row--dark' : ''}`}
              >
                <h4 className="link-button-showcase__label">
                  {LINK_BUTTON_PREVIEW_STATE_LABELS[state]}
                </h4>
                <LinkButton
                  label="Link button"
                  previewState={state}
                  darkMode={darkMode}
                />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Link button"
        sections={propertySections}
      />
    </>
  )
}

export default LinkButtonShowcase
